/**
 * API pública del SDK — CONTRATO CONGELADO (Ola 0, §3.2).
 * Tipos que consume el comercio host. Ver docs/agent-orchestration.md §3.2.
 */
import type {
  FinvaError,
  FinvaMode,
  FinvaProduct,
  FinvaTheme,
  InitPayload,
  SubmitData,
} from '../transport/protocol';

/**
 * Flujo de compra. Duplicado a propósito del `FinvaFlow` del lado app
 * (`types/credit-application.ts`): el bundle esbuild del SDK host y la app
 * Next no comparten módulo, igual que `InitPayload`. Mantener ambos idénticos.
 */
export type FinvaFlow = 'financed' | 'cash' | 'card';

export const FINVA_FLOWS: readonly FinvaFlow[] = ['financed', 'cash', 'card'];

export interface FinvaOptions {
  /** Default `'es-MX'`. */
  locale?: string;
  /**
   * Origin del iframe (dónde vive `/aplicacion` y `/api/sdk/config`). Se
   * auto-detecta del `<script src>` que cargó el bundle, así un comercio que
   * carga el SDK desde su CDN no necesita pasarlo. Overridear sólo para
   * dev/staging; fallback final `https://app.finva.mx`.
   */
  origin?: string;
  /**
   * Origin de la página host que identifica al tenant. Por defecto se
   * auto-detecta de `window.location.origin`; overridear sólo para pruebas o
   * SSR donde no hay `window`.
   */
  host?: string;
}

export interface BrickSettings {
  initialization: {
    brand?: string;
    mode?: FinvaMode;
    prefill?: InitPayload['prefill'];
    /** Producto a financiar. El host lo conoce; el iframe no tiene catálogo. */
    product?: FinvaProduct;
    /**
     * Flujo de compra a ofrecer. Viaja al iframe como query param `flow=`
     * (NO toca el contrato congelado `InitPayload` — canal postMessage).
     *   - `financed`: crédito + buró (default histórico).
     *   - `cash`: contado, registro mínimo sin buró.
     *   - `card`: pago con tarjeta (Mercado Pago — placeholder "Próximamente").
     * Si se omite, el iframe muestra un selector de flujo (`availableFlows`).
     */
    flow?: FinvaFlow;
    /**
     * Flujos que el selector ofrece cuando `flow` no se fija. Default: los tres.
     * Se serializa como query param `flows=` (comma-joined).
     */
    availableFlows?: FinvaFlow[];
  };
  customization?: {
    theme?: FinvaTheme;
    primaryColor?: string;
    secondaryColor?: string;
  };
  callbacks?: {
    onReady?: () => void;
    onSubmit?: (data: SubmitData) => void;
    onApproved?: (solicitud: string) => void;
    onError?: (error: FinvaError) => void;
  };
  /**
   * IDs de tracking del merchant (opcionales). Si se setean, el paquete inyecta
   * los snippets de Meta Pixel y/o Google Ads dentro del iframe y espeja ahí los
   * eventos del flujo de crédito (con props NO sensibles — sin PII). Los IDs NO
   * son secretos (viven en el HTML del sitio del merchant) → viajan al iframe
   * como query params públicos.
   */
  analytics?: {
    /** Meta/Facebook Pixel ID (numérico, ~15 dígitos). Se normaliza a dígitos. */
    metaPixelId?: string;
    /** Google Ads ID `AW-XXXXXXX` o `AW-XXXXXXX/label` (conversión). */
    googleAdsId?: string;
  };
}

export interface BrickController {
  unmount(): void;
  open(): void;
  close(): void;
  update(partial: Partial<BrickSettings>): void;
}